import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { ColumnSortOption } from '../../../Component/Model/Dto/ColumnSortOption';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { UserAtom } from '../../../State/user.Atom';
import { VerificationCompanyAtom } from '../../../State/VerificationCompany.Atom';
import {
    SendApplicationLinkDtoIn,
    VAClientApplicationData,
    VAClientApplicationSearchRequest, VerificationAgencyClientApplication
} from "../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication";
import {
    VerificationAgencyClientApplicationService
} from "../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService";
import { VAClientApplicationTableMapper } from "./VerificationAgencyClientApplicationTableMapper";
import { VerificationAgencyClientApplicationTable } from "./VerificationAgencyClientApplicationTable";
import { isArray } from "lodash";
import { EmailApplicationLinkDialog } from './EmailApplicationLinkDialog';
import { verificationAgency } from '../../../Component/Common/Constants/routes';
import { VerificationAgencyClientService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService';

interface IVAClientApplicationSearch {
    showAll?: boolean;
}

export const VerificationAgencyClientApplicationPage = (props: IVAClientApplicationSearch) => {

    const [company, setCompany] = useRecoilState(VerificationCompanyAtom);
    const [VAClientApplicationData, setVAClientApplicationData] = useState<VAClientApplicationData[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);

    const user = useRecoilValue(UserAtom);
    const history = useHistory();
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false); //Delete
    const [showSendConfirmationDialog, setShowSendConfirmationDialog] = useState<boolean>(false);
    const confirmDeleteMessage = "Are you sure you want to delete this client application?";
    const [selectedApplication, setApplication] = useState<VerificationAgencyClientApplication.ClientApplicationDetailsDto>(defaultObject);
    const allClients = props.showAll ?? false;
    const [isSending, setIsSending] = useState<boolean>(false);

    const VAClientApplicationSearchRequest: VAClientApplicationSearchRequest = {
        Draw: 2,
        PageNumber: pageNumber,
        PageSize: entriesPerPage.defaultValue!,
        ColumnSortOptions: columnSortOptions,
        SearchValue: searchValue,
        RegistrationId: user.registrationId,
        VAClientId: props.showAll ? undefined : company.vaClientId
    };

    const VAClientApplicationDataSearch = async () => {

        if (user.registrationId != null && user.registrationId !== "") {
            setIsLoading(true);
            try {
                const vaClientApplicationSearchResult = await VerificationAgencyClientApplicationService.VAClientApplicationSearch(VAClientApplicationSearchRequest);
                setVAClientApplicationData(vaClientApplicationSearchResult.data.results); // Set the API data, not the table data
                setFilteredItemsCount(vaClientApplicationSearchResult.data.filteredItemsCount);
                setTotalItemsCount(vaClientApplicationSearchResult.data.totalItemsCount);
            } catch (error) {
                setIsLoading(false);
                setFilteredItemsCount(1);
                setTotalItemsCount(1);
            } finally {
                setIsLoading(false);
            }
        }
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
    }

    const showDeleteConfirmation = (application: VerificationAgencyClientApplication.ClientApplicationDetailsDto) => {
        setApplication(application);
        setShowConfirmationDialog(true);
    }

    const showSendApplicationLinkConfirmation = (application: VerificationAgencyClientApplication.ClientApplicationDetailsDto) => {
        setCompany({ ...company, vaClientId: application.vaClientId ?? 0, applicationId: application.vaClientApplicationId ?? 0, company: application.name ?? "" });
        setApplication(application);
        setShowSendConfirmationDialog(true);
    }

    const handleDelete = async () => {
        try {
            await VerificationAgencyClientApplicationService.UpdateEntry(selectedApplication);
            toast.success("Successfully deleted client application");
            VAClientApplicationDataSearch();
        } catch (error) {
            console.log(error)
        } finally {
            handleCloseConfirmation();
        }
    }

    const handleSendApplication = async (contactEmail: string, contactName: string) => {
        try {
            setIsSending(true);
            setShowConfirmationDialog(false);
            var model: SendApplicationLinkDtoIn = { ContactEmail: contactEmail, ContactName: contactName, ApplicationId: selectedApplication.vaClientApplicationId ?? 0 };
            await VerificationAgencyClientApplicationService.SendApplicationLink(model); //Create token
            setIsSending(false);

            toast.success("Successfully sent client application");
            VAClientApplicationDataSearch();
        } catch (error) {
            console.log(error)
        } finally {
            setShowSendConfirmationDialog(false);
        }
    }

    useEffect(() => {
        setCompany({ ...company,  applicationId: -1 });
    } , []);

    useEffect(() => {
        VAClientApplicationDataSearch();
    }, [entriesPerPage, pageNumber, searchValue, columnSortOptions, user.registrationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (searchValue: string) => {
        setSearchValue(searchValue);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const addSelectedClientApplication = async () => {
        try {
            const requestData = {
                vaClientId: company.vaClientId,
            } as VerificationAgencyClientApplication.ClientApplicationDetailsDto;

            const result = await VerificationAgencyClientApplicationService.CreateEntry(requestData);

            toast.success(`Application created`);
            handleRouteToApplication(company.vaClientId, result.data.vaClientApplicationId, company.company);
        } catch (error: any) {
            console.log(error);
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    const handleRouteToApplication = async (companyId: number, applicationId: number, name: string) => {
        try {
            const companyDetails = await VerificationAgencyClientService.GetById(companyId);
            setCompany({ ...company, vaClientId: companyId, applicationId: applicationId, company: name, registrationNumber : companyDetails.data.registrationNumber, vatNumber: companyDetails.data.vatNumber });
            history.push(`/${verificationAgency.company}/${verificationAgency.applications}/serviceRequired`);
        } catch (error) {
            console.log(error);
        }
    }

    const tableData = VAClientApplicationTableMapper(VAClientApplicationData, showDeleteConfirmation, showSendApplicationLinkConfirmation, handleRouteToApplication, allClients);

    return (
        <div className='center'>
            <Grid container>
                <Grid item xs={12}>
                    <VerificationAgencyClientApplicationTable
                        results={tableData!}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredItemsCount}
                        totalItemsCount={totalItemsCount}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        searchValueChanged={searchValueChanged}
                        isLoading={isLoading}
                        columnSortingChanged={columnSortingChanged}
                        addSelectedClientApplication={addSelectedClientApplication}
                        clientSelected={props.showAll === false || props.showAll === undefined}
                    />
                    <ConfirmationDialog
                        showDialog={showConfirmationDialog}
                        handleAccept={handleDelete}
                        handleCancel={handleCloseConfirmation}
                        heading={"Confirm Delete"}
                        message={confirmDeleteMessage}
                    />
                    {showSendConfirmationDialog &&
                        <EmailApplicationLinkDialog
                            onCloseModal={() => setShowSendConfirmationDialog(false)}
                            showModal={showSendConfirmationDialog}
                            vaClientId={selectedApplication.vaClientId ?? 0}
                            isLoading={isSending}
                            onSend={handleSendApplication}
                        />
                    }
                </Grid>
            </Grid>
        </div>
    )
}
